/**
 * DownloadPackageDialog - progress dialog for game download
 */

import type { ReactNode } from 'react';
import { abortDownloadPackage, downloadGamePackage, getDownloadProgress, setDownloadProgressCallback, type DownloadProgress } from '../core/DownloadPackage';

// React is available globally from CDN
const React = (window as any).React;
const ReactComponent = React.Component;

interface DownloadPackageDialogProps {
	onClose: () => void;
}

interface DownloadPackageDialogState {
	progress: DownloadProgress;
}

class DownloadPackageDialog extends ReactComponent<DownloadPackageDialogProps, DownloadPackageDialogState> {
	constructor(props: DownloadPackageDialogProps) {
		super(props);
		this.state = {
			progress: getDownloadProgress()
		};
	}

	componentDidMount(): void {
		setDownloadProgressCallback((progress) => {
			this.setState({ progress });
			if (progress.state === 'success') {
				this.props.onClose();
			}
		});

		if (this.state.progress.state === 'idle' || this.state.progress.state === 'error' || this.state.progress.state === 'aborted' || this.state.progress.state === 'success') {
			void downloadGamePackage();
		}
	}

	componentWillUnmount(): void {
		setDownloadProgressCallback(null);
	}

	handleCancel = (): void => {
		abortDownloadPackage();
		this.props.onClose();
	};

	render(): ReactNode {
		const { progress } = this.state;
		const total = progress.total ?? 0;
		const percent = total > 0 ? Math.min(100, Math.round((progress.processed / total) * 100)) : 0;
		const isIndeterminate = progress.state === 'collecting' || progress.total === null;

		return React.createElement('div', { className: 'download-package-dialog' },
			React.createElement('div', { className: 'download-package-status' }, this.renderStatusText()),
			React.createElement('div', { className: `download-package-progress ${isIndeterminate ? 'indeterminate' : ''}` },
				React.createElement('div', {
					className: 'download-package-progress-fill',
					style: { width: isIndeterminate ? '100%' : `${percent}%` }
				})
			),
			React.createElement('div', { className: 'download-package-count' },
				total > 0 ? `${progress.processed} / ${total} files` : 'Preparing file list...'
			),
			React.createElement('div', { className: 'download-package-actions' },
				React.createElement('button', {
					className: 'btn',
					onClick: this.handleCancel
				}, progress.state === 'downloading' || progress.state === 'collecting' || progress.state === 'zipping' ? 'Cancel' : 'Close')
			)
		);
	}

	private renderStatusText(): string {
		const { progress } = this.state;
		if (progress.message) return progress.message;
		switch (progress.state) {
			case 'collecting':
				return 'Collecting resources...';
			case 'downloading':
				return 'Downloading resources...';
			case 'zipping':
				return 'Creating zip archive...';
			case 'success':
				return 'Download ready.';
			case 'error':
				return 'Download failed.';
			case 'aborted':
				return 'Download cancelled.';
			default:
				return 'Preparing download...';
		}
	}
}

(window as any).DownloadPackageDialog = DownloadPackageDialog;
export default DownloadPackageDialog;
